import { motion } from 'framer-motion';
import styled, { css } from 'styled-components';
import React, { HTMLAttributes, RefObject, useMemo } from 'react';
import { media } from 'src/styles/designs';
import { useTranslate } from 'react-t7e';
import { FastField, FieldProps } from 'formik';
import { Button } from 'src/components/common/Button';
import { MediaDirect } from 'vendor/mods/media-breakpoints-fresnel/components/Media';
import { FilterType } from 'src/components/filters/types';
import { FilterSearchInput } from 'src/components/filters/FilterSearchInput';
import { LockScroll } from 'vendor/hooks/useLockScroll';
import { Close } from 'src/components/icons/Close';
import { motionFadeFromLeftMoreFaster } from 'src/utils/motion/transitions';
import { useClientReady } from '@summer/jst-react';
import { dirRTL } from 'src/styles/theme';

const SearchQueryInputContainer = styled.div`
  display: grid;
  grid-gap: ${({ theme }) => theme.spacing.gap}px;
  grid-template-areas: 'input filtersButton' 'filters filters';

  ${media.w.greaterThanOrEqual.desktop} {
    grid-template-areas: 'input' 'filters';
  }
`;

const extendMargins = css`
  margin-left: calc(-1 * var(--left-usable-margin));
  margin-right: calc(-1 * var(--right-usable-margin));
  padding-left: var(--left-usable-margin);
  padding-right: var(--right-usable-margin);
  ${dirRTL} {
    margin-right: calc(-1 * var(--left-usable-margin));
    margin-left: calc(-1 * var(--right-usable-margin));
    padding-right: var(--left-usable-margin);
    padding-left: var(--right-usable-margin);
  }
`;

const MobileFiltersContainer = styled.div<{ $top?: number }>`
  z-index: 10;
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  padding-right: var(--right-usable-margin);
  padding-left: var(--left-usable-margin);
  ${dirRTL} {
    padding-left: var(--right-usable-margin);
    padding-right: var(--left-usable-margin);
  }

  display: grid;
  grid-template-areas: 'header' 'filters';
  grid-template-rows: auto 1fr;

  ${SearchQueryInputContainer} {
    background: ${({ theme }) => theme.palette.background};
    padding-top: 20px;
    ${extendMargins};
  }
`;

const MobileFiltersScrollableArea = styled(motion.div)`
  background: ${({ theme }) => theme.palette.background};
  overflow: auto;
  ${extendMargins};
  padding-bottom: var(--footer-static-height);
`;

const StyledMediaDirect = styled(MediaDirect)`
  grid-area: filtersButton;
`;

export const FiltersBarMobile = ({
  onClose,
  children,
  containerRef,
  ...props
}: HTMLAttributes<HTMLDivElement> & {
  onClose: () => void;
  containerRef: RefObject<HTMLDivElement>;
}) => {
  const { _ } = useTranslate();

  const clientReady = useClientReady();
  const position = useMemo(
    () =>
      (clientReady && containerRef.current?.getBoundingClientRect()) || null,
    [containerRef, clientReady],
  );

  return (
    <MobileFiltersContainer {...props} style={{ top: position?.top }}>
      <LockScroll />

      <SearchQueryInputContainer>
        <FilterSearchInput
          filter={{
            queryField: 'q',
            type: FilterType.SearchInput,
            title: _('Write a phrase'),
          }}
        />
        <FastField>
          {({ form }: FieldProps<unknown>) => (
            <StyledMediaDirect lessThan="desktop">
              <Button
                checked
                kind="text"
                icon={Close}
                onClick={() => {
                  onClose();
                  form.submitForm();
                }}
                iconProps={{
                  position: 'right',
                }}
              >
                {_('Filters')}
              </Button>
            </StyledMediaDirect>
          )}
        </FastField>
      </SearchQueryInputContainer>
      <MobileFiltersScrollableArea {...motionFadeFromLeftMoreFaster}>
        {children}
      </MobileFiltersScrollableArea>
    </MobileFiltersContainer>
  );
};
